import { useSetState } from 'ahooks';
import { useMemo, useRef } from 'react';
import { useCommunityInteraction } from '@/app/components/shared/Community';
import { TRACE_EVENT_ID, TRACE_EVENT_NAME } from '@/app/constants';
import { useCreationNavigation, useFetch, useMediaMetrics } from '@/app/hooks';
import {
  CommunityEnum,
  type ExploreTab,
  type InspirationItem,
  type SelectedAsset,
  type UpdateInteractionStatusParams,
} from '@/app/types';
import { getAssetInfo, reportEvent, transformExploreDetail } from '@/app/utils';
import { usePathname } from '@/i18n/routing';
import { apiGetDetail } from '../apis';
import { COMMUNITY_SEARCH_OPTIONS } from '../constant';
import type { CommunitySearchOption } from '../types';

const PAGE_SIZE = 36;

export type CommunityState = {
  type: CommunitySearchOption;
  page: number;
  total: number;
  items: InspirationItem[];
  deleteDialogCreation?: InspirationItem;
  selectedAsset?: SelectedAsset;
};

export type UseCommunityParams = {
  initialType?: CommunitySearchOption;
  fetchData: (params: Record<string, unknown>) => Promise<{ data?: { total?: number; list?: InspirationItem[] } }>;
  searchOptions: Record<string, string>[];
  infinityList?: boolean;
};

export type EmptyStateProps = {
  text: string;
  showCreateButton: boolean;
};

export type VoteActionType = 'vote' | 'unvote';

export const useCommunity = ({
  initialType = COMMUNITY_SEARCH_OPTIONS.ALL,
  fetchData,
  searchOptions,
  infinityList,
}: UseCommunityParams) => {
  const navigationDirection = useRef<'prev' | 'next' | null>(null);
  const pathname = usePathname();
  const [state, setState] = useSetState<CommunityState>({
    type: initialType,
    page: 1,
    total: 0,
    items: [],
    deleteDialogCreation: undefined,
    selectedAsset: undefined,
  });

  const { handleEnter, handleLeave, incrementCompleteTimes } = useMediaMetrics({
    callback: (detailStayTime: number, completeTimes: number) => {
      const { type, traceId = '', id = '' } = getAssetInfo(state.selectedAsset) || {};
      type &&
        reportEvent(TRACE_EVENT_NAME[type], {
          [TRACE_EVENT_ID[type]]: id,
          interaction_type: 'view_detail',
          interaction_position: pathname,
          trace_id: traceId,
          detail_staytime: detailStayTime,
          complete_times: completeTimes,
          content_type: 'video',
        });
    },
  });

  const { loading, error } = useFetch(
    () =>
      fetchData({
        'pager.page': state.page - 1,
        'pager.pagesz': PAGE_SIZE,
        types:
          state.type === COMMUNITY_SEARCH_OPTIONS.ALL
            ? []
            : state.type === 'media_asset'
              ? ['image_asset', 'media_asset', 'mini_video'] // 现在灵感对应 图片灵感和视频灵感
              : [state.type],
      }),
    {
      refreshDeps: [state.page, state.type],
      ready: state.type !== CommunityEnum.PostReferences,
      onSuccess: ({ data }) => {
        if (data) {
          const { total, list = [] } = data;
          setState({
            total: total ?? 0,
            items: infinityList ? state.items.concat(list) : list,
          });
          if (list && navigationDirection.current) {
            handlePageChange(
              navigationDirection.current,
              list.map((item: InspirationItem) => ({
                ...item,
                id:
                  item.image_asset?.id ??
                  item.media_asset?.id ??
                  item.short_film?.id ??
                  item.tutorial?.id ??
                  item.mini_video?.id ??
                  '',
              })),
            );
            navigationDirection.current = null;
          }
        }
      },
    },
  );

  const { run: getDetailInfo } = useFetch(
    (id: string) =>
      apiGetDetail({
        path: {
          id,
        },
      }),
    {
      manual: true,
      onSuccess: ({ data }) => {
        if (data) {
          handleEnter();
          const { type, value } = transformExploreDetail(data) || {};
          type &&
            setState({
              selectedAsset: {
                [type]: value,
              },
            });
        }
      },
    },
  );

  const totalPages = useMemo(() => Math.ceil((state.total ?? 0) / PAGE_SIZE), [state.total]);

  const { isPrevButtonDisabled, isNextButtonDisabled, handleSelectedCreation, handleNavigation, handlePageChange } =
    useCreationNavigation({
      items: state.items.map((item) => ({
        ...item,
        id:
          item.image_asset?.id ??
          item.media_asset?.id ??
          item.short_film?.id ??
          item.tutorial?.id ??
          item.mini_video?.id ??
          '',
      })),
      page: state.page,
      totalPages,
      loading,
      onPageChange: (page) => setState({ page }),
      onCreationSelect: (creation) => creation.id && getDetailInfo(creation.id),
    });

  const onNavigate = (type: 'prev' | 'next') => {
    handleLeave();
    const direction = handleNavigation(type);
    if (direction) {
      navigationDirection.current = direction;
    }
  };

  /**
   * 灵感/短片/教程 收藏/点赞之后更新状态
   */
  const updateInteractionStatus = ({ id, obj_type, voted, collected }: UpdateInteractionStatusParams) => {
    const type = obj_type as ExploreTab;
    const updatedItems = [...state.items];

    const item = updatedItems.find((item) => item[type]?.id === id);

    if (typeof voted === 'boolean' && item?.[type]?.react_info) {
      item[type].react_info = {
        ...item[type].react_info,
        vote_count: (item[type].react_info?.vote_count ?? 0) + (voted ? 1 : -1),
        voted,
      };
    }
    if (typeof collected === 'boolean' && item?.[type]?.react_info) {
      item[type].react_info = {
        ...item[type].react_info,
        collect_count: (item[type].react_info?.collect_count ?? 0) + (collected ? 1 : -1),
        collected,
      };
    }
    setState({ items: updatedItems });
  };

  const { vote, unVote, collect, unCollect } = useCommunityInteraction({
    onInteractionStatusChange: (params) => updateInteractionStatus(params as UpdateInteractionStatusParams),
  });

  return {
    state,
    setState,
    loading,
    error,
    searchOptions,
    isPrevButtonDisabled,
    isNextButtonDisabled,
    onNavigate,
    handleSelectedCreation,
    vote,
    unVote,
    collect,
    unCollect,
    getDetailInfo,
    incrementCompleteTimes,
    handleLeave,
  };
};
